import { useState } from 'react';
import { Lock, Mail, Key, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { signIn, resetPassword } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await signIn(email, password);
    } catch (error: any) {
      setMessage(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await resetPassword(email);
      setMessage('Password reset email sent! Check your inbox.');
    } catch (error: any) {
      setMessage(error.message || 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-700 to-teal-900 flex items-center justify-center p-6">
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 w-full max-w-md border border-white/20">
        <div className="flex items-center justify-center mb-6">
          <div className="bg-amber-500 p-4 rounded-full">
            <Lock className="w-8 h-8 text-white" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-white text-center mb-2">
          {showReset ? 'Reset Password' : 'CRM Admin Login'}
        </h1>
        <p className="text-white/70 text-center mb-6">
          {showReset ? 'Enter your email to receive reset instructions' : 'Access the Prayer Times CRM Dashboard'}
        </p>

        {message && (
          <div className={`mb-4 p-3 rounded-lg ${message.includes('success') || message.includes('sent') ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'}`}>
            {message}
          </div>
        )}

        <form onSubmit={showReset ? handleReset : handleLogin} className="space-y-4">
          <div>
            <label className="block text-white/80 mb-2 text-sm font-semibold">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                className="w-full bg-white/10 text-white rounded-lg pl-11 pr-4 py-3 border border-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-white/40"
              />
            </div>
          </div>

          {!showReset && (
            <div>
              <label className="block text-white/80 mb-2 text-sm font-semibold">Password</label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full bg-white/10 text-white rounded-lg pl-11 pr-4 py-3 border border-white/30 focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-white/40"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-gray-500 text-white font-bold py-3 rounded-lg transition-all transform hover:scale-105 disabled:scale-100"
          >
            {loading ? 'Processing...' : showReset ? 'Send Reset Link' : 'Login to CRM'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setShowReset(!showReset);
              setMessage('');
            }}
            className="text-amber-300 hover:text-amber-200 text-sm font-semibold flex items-center gap-2 mx-auto transition-colors"
          >
            {showReset ? (
              <>
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </>
            ) : (
              'Forgot Password?'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
